import { create } from "zustand"

export type Theme = "dark" | "light"

const SIDEBAR_KEY = "it2tv:sidebarCollapsed"
const THEME_KEY = "it2tv:theme"
const DRAWER_TAB_KEY = "it2tv:drawerTab"
const BOTTOM_DOCK_COLLAPSED_KEY = "it2tv:workspaceBottomDockCollapsed"
const LEGACY_CANVAS_PANEL_COLLAPSED_KEY = "canvas-panel-collapsed"

export type DrawerTab = "settings" | "jobs_history" | "agents" | "versions" | "notes"

const DRAWER_TABS: DrawerTab[] = ["agents", "settings", "jobs_history", "notes"]

function readSidebarPref(): boolean | null {
  try {
    const v = localStorage.getItem(SIDEBAR_KEY)
    if (v === "0") return false
    if (v === "1") return true
    return null
  } catch {
    return null
  }
}

/** No saved choice: collapsed inside a project, expanded elsewhere. */
export function resolveSidebarCollapsed(pref: boolean | null, inProject: boolean): boolean {
  return pref ?? inProject
}

function readThemePref(): Theme {
  try {
    const v = localStorage.getItem(THEME_KEY)
    return v === "light" ? "light" : "dark"
  } catch {
    return "dark"
  }
}

export function hasThemePref(): boolean {
  try {
    const v = localStorage.getItem(THEME_KEY)
    return v === "light" || v === "dark"
  } catch {
    return false
  }
}

function readDrawerTabPref(): DrawerTab {
  try {
    const v = localStorage.getItem(DRAWER_TAB_KEY)
    if (v && DRAWER_TABS.includes(v as DrawerTab)) return v as DrawerTab
  } catch {
    /* SSR / private mode */
  }
  return "agents"
}

function readBottomDockCollapsedPref(): boolean {
  try {
    const next = localStorage.getItem(BOTTOM_DOCK_COLLAPSED_KEY)
    if (next === "1" || next === "0") return next === "1"
    const legacy = localStorage.getItem(LEGACY_CANVAS_PANEL_COLLAPSED_KEY)
    if (legacy === "1" || legacy === "0") {
      localStorage.setItem(BOTTOM_DOCK_COLLAPSED_KEY, legacy)
      return legacy === "1"
    }
  } catch {
    /* SSR / private mode */
  }
  return false
}

function writeThemePref(t: Theme) {
  try {
    localStorage.setItem(THEME_KEY, t)
  } catch {
    /* SSR / private mode */
  }
}

function writeSidebarPref(v: boolean) {
  try {
    localStorage.setItem(SIDEBAR_KEY, v ? "1" : "0")
  } catch {
    /* SSR / private mode */
  }
}

function writeDrawerTabPref(tab: DrawerTab) {
  try {
    localStorage.setItem(DRAWER_TAB_KEY, tab)
  } catch {
    /* SSR / private mode */
  }
}

function writeBottomDockCollapsedPref(v: boolean) {
  try {
    localStorage.setItem(BOTTOM_DOCK_COLLAPSED_KEY, v ? "1" : "0")
  } catch {
    /* SSR / private mode */
  }
}

type UiState = {
  theme: Theme
  commandOpen: boolean
  jobsOpen: boolean
  drawerOpen: boolean
  drawerTab: DrawerTab
  mobileNavOpen: boolean
  sidebarCollapsedPref: boolean | null
  /**
   * Shot panel leaves the icon rail visible and starts it collapsed. While it is open the
   * toggle expands the rail for this session only — the saved preference stays as it was.
   */
  shotPanelOpen: boolean
  shotPanelSidebarExpanded: boolean
  workspaceBottomDockCollapsed: boolean
  pendingAgentAutoSend: PendingAgentAutoSend | null
  setTheme: (t: Theme) => void
  toggleCommand: () => void
  setJobsOpen: (v: boolean) => void
  setDrawerOpen: (v: boolean) => void
  setDrawerTab: (tab: DrawerTab) => void
  setMobileNavOpen: (v: boolean) => void
  toggleSidebar: (inProject?: boolean) => void
  setSidebarCollapsed: (v: boolean) => void
  setShotPanelOpen: (open: boolean) => void
  setWorkspaceBottomDockCollapsed: (v: boolean) => void
  setPendingAgentAutoSend: (v: PendingAgentAutoSend | null) => void
  clearPendingAgentAutoSend: () => void
}

export type AgentHandoffSurface = "characters" | "locations" | "props" | "shot" | "tasks"

export type PendingAgentAutoSend = {
  projectId: number
  surface: AgentHandoffSurface
  message: string
  selectedEntityId?: number
  selectedShotId?: number
}

export function useSidebarCollapsed(inProject: boolean): boolean {
  return useUiStore((s) =>
    s.shotPanelOpen ? !s.shotPanelSidebarExpanded : resolveSidebarCollapsed(s.sidebarCollapsedPref, inProject),
  )
}

export const useUiStore = create<UiState>((set) => ({
  theme: readThemePref(),
  commandOpen: false,
  jobsOpen: false,
  drawerOpen: false,
  drawerTab: readDrawerTabPref(),
  mobileNavOpen: false,
  sidebarCollapsedPref: readSidebarPref(),
  shotPanelOpen: false,
  shotPanelSidebarExpanded: false,
  workspaceBottomDockCollapsed: readBottomDockCollapsedPref(),
  pendingAgentAutoSend: null,
  setTheme: (theme) => {
    writeThemePref(theme)
    set({ theme })
  },
  toggleCommand: () => set((s) => ({ commandOpen: !s.commandOpen })),
  setJobsOpen: (jobsOpen) => set({ jobsOpen }),
  setDrawerOpen: (drawerOpen) => set({ drawerOpen }),
  setDrawerTab: (drawerTab) => {
    writeDrawerTabPref(drawerTab)
    set({ drawerTab })
  },
  setMobileNavOpen: (mobileNavOpen) => set({ mobileNavOpen }),
  toggleSidebar: (inProject = false) =>
    set((s) => {
      if (s.shotPanelOpen) return { shotPanelSidebarExpanded: !s.shotPanelSidebarExpanded }
      const next = !resolveSidebarCollapsed(s.sidebarCollapsedPref, inProject)
      writeSidebarPref(next)
      return { sidebarCollapsedPref: next }
    }),
  setSidebarCollapsed: (sidebarCollapsed) => {
    writeSidebarPref(sidebarCollapsed)
    set({ sidebarCollapsedPref: sidebarCollapsed })
  },
  setShotPanelOpen: (shotPanelOpen) => set({ shotPanelOpen, shotPanelSidebarExpanded: false }),
  setWorkspaceBottomDockCollapsed: (workspaceBottomDockCollapsed) => {
    writeBottomDockCollapsedPref(workspaceBottomDockCollapsed)
    set({ workspaceBottomDockCollapsed })
  },
  setPendingAgentAutoSend: (pendingAgentAutoSend) => set({ pendingAgentAutoSend }),
  clearPendingAgentAutoSend: () => set({ pendingAgentAutoSend: null }),
}))
